import React, { useEffect, useMemo, useState } from 'react';
import { useFilterContext, DropDownFilterLayoutType, SelectLayout } from 'framework/list';
import { BaseInputErrors } from 'framework/ui/layout';
import { Option as OptionType } from 'framework/form';
import IconButton from '@mui/material/IconButton';
import FilterAltOutlined from '@mui/icons-material/FilterAltOutlined';
import { Box, Menu } from '@mui/material';
import PersonTimelineFilterActions from './PersonTimelineFilterActions';

const PersonTimelineFilterButtonLayout: DropDownFilterLayoutType = props => {
  const {
    options: initialOptions = [],
    errorMessages,
    multiple = true,
    value: initialValue = '',
    source,
    onChangeCallback,
  } = props;
  const { apply: applyFilters } = useFilterContext();
  const [shouldApplyFilter, setShouldApplyFilter] = useState(false);
  const [selectedValues, setSelectedValues] = useState<OptionType[]>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const options = useMemo(
    () =>
      (initialOptions as OptionType[]).map(({ value, ...optionProps }) => ({
        ...optionProps,
        value: value ? String(value) : value,
      })),
    [initialOptions],
  );
  const isContainValue = (value: string, allValues: string | string[]) =>
    multiple ? allValues?.includes(value) : String(allValues) === value;

  const { resetFilter } = useFilterContext();
  const [isMenuOpened, setIsMenuOpen] = useState(false);

  const toggleMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isMenuOpened) {
      resetFilter(source);
    }
    setIsMenuOpen(prevValue => !prevValue);
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    if (!multiple && isMenuOpened && shouldApplyFilter) {
      closeMenu();
      applyFilters();
      setShouldApplyFilter(false);
    }

    setSelectedValues(options.filter(({ value }) => isContainValue(value as string, initialValue)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, options, setShouldApplyFilter]);

  return (
    <Box sx={{ display: 'inline-flex' }}>
      <IconButton
        onClick={toggleMenu}
        color={selectedValues?.some(Boolean) ? 'primary' : 'default'}
        aria-haspopup="true"
        aria-controls={isMenuOpened ? 'person-filter-menu' : undefined}
        aria-expanded={isMenuOpened ? 'true' : undefined}
      >
        <FilterAltOutlined />
      </IconButton>

      {isMenuOpened && (
        <Menu
          id="person-filter-menu"
          open={isMenuOpened}
          onClose={closeMenu}
          className="filter-input"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box
            sx={{
              minWidth: '200px',
              '& .dropdown_btn_box': {
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '4px 12px 0 0',
              },
            }}
          >
            <SelectLayout
              {...props}
              options={options}
              selectedValues={selectedValues}
              onChangeCallback={(value: any) => {
                onChangeCallback(value);
                if (!multiple) {
                  setShouldApplyFilter(true);
                }
              }}
            />

            <BaseInputErrors errorMessages={errorMessages} />
            {multiple && <PersonTimelineFilterActions onChangeCallback={toggleMenu} />}
          </Box>
        </Menu>
      )}
    </Box>
  );
};

export default PersonTimelineFilterButtonLayout;
